import { getConfig } from './config';
import { store, type AccountView } from './store';
import { markSubsAuthRedirectNotice } from './subsAuthNotice';

function requestHostMobileMenuClose(view?: AccountView): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (view) {
    window.dispatchEvent(new CustomEvent('sw:overlay-open-intent', { detail: { view } }));
  }
  const clearCls = (el: Element | null, cls: string) => { if (el && el.classList.contains(cls)) el.classList.remove(cls); };
  const navToggle = document.getElementById('sl-nav-toggle') as HTMLInputElement | null;
  if (navToggle && navToggle.type === 'checkbox' && navToggle.checked) {
    navToggle.checked = false;
    navToggle.dispatchEvent(new Event('change', { bubbles: true }));
  }
  clearCls(document.documentElement, 'menu_open');
  clearCls(document.documentElement, 'mobile-menu-open');
  clearCls(document.documentElement, 'sl-mobile-menu-open');
  clearCls(document.body, 'menu_open');
  clearCls(document.body, 'mobile-menu-open');
  clearCls(document.body, 'sl-mobile-menu-open');
  document.querySelectorAll('.sl-header .sl-mobile-catalog-panel.sl-open').forEach((panel) => {
    panel.classList.remove('sl-open');
    panel.setAttribute('aria-hidden', 'true');
  });
  document.querySelectorAll('.sl-header [data-sl-mobile-catalog-open]').forEach((btn) => {
    btn.setAttribute('aria-expanded', 'false');
  });
  const state = (document.body.getAttribute('data-ui-state') || '').trim();
  if (state !== '') {
    const tokens = state.split(/\s+/).filter((t) => t !== '' && t !== 'menu_open' && t !== 'mobile_menu_open');
    document.body.setAttribute('data-ui-state', tokens.join(' '));
  }
  window.dispatchEvent(new CustomEvent('sw:request-host-menu-close'));
}

function resolveCartOpenMode(): 'page' | 'modal' | 'side_drawer' {
  const raw = String(getConfig().cartOpenMode ?? '').trim().toLowerCase();
  if (raw === 'page' || raw === 'modal' || raw === 'side_drawer') return raw;
  if (raw === 'drawer' || raw === 'drawler' || raw === 'side-drawer' || raw === 'side drawer') return 'side_drawer';
  return 'modal';
}

const LISTING_QUERY_KEYS = ['category', 'subcategory', 'symptom', 'sort', 'page'] as const;

/** Strip catalog filter params so account/auth redirects do not render listing HTML. */
export function stripListingQueryParams(rawUrl: string): string {
  if (typeof window === 'undefined') return rawUrl;
  try {
    const u = new URL(rawUrl, window.location.origin);
    for (const key of LISTING_QUERY_KEYS) {
      u.searchParams.delete(key);
    }
    return u.pathname + u.search + u.hash;
  } catch {
    return rawUrl;
  }
}

const ACCOUNT_PAGE_VIEWS = new Set<NonNullable<AccountView>>([
  'account', 'orders', 'tickets', 'subscriptions', 'bonus-account',
  'bonus-transactions', 'referral-tree', 'account-delete',
  'signup', 'checkout-login', 'reset-password',
]);

function sanitizeViewRedirectUrl(rawUrl: string, view: AccountView): string {
  if (!view || !ACCOUNT_PAGE_VIEWS.has(view)) return rawUrl;
  return stripListingQueryParams(rawUrl);
}

function appendLangToUrl(rawUrl: string): string {
  if (typeof window === 'undefined') return rawUrl;
  try {
    const target = new URL(rawUrl, window.location.origin);
    const cur = new URL(window.location.href);
    const lang = cur.searchParams.get('lang')
      || (() => {
        const m = document.cookie.match(/(?:^|;\s*)sl_lang=([^;]+)/);
        return m?.[1] ? decodeURIComponent(m[1]) : '';
      })();
    if (lang) target.searchParams.set('lang', lang);
    return target.pathname + target.search + target.hash;
  } catch {
    return rawUrl;
  }
}

export function openCatalogView() {
  const catalogUrl = getConfig().viewUrls?.catalog || '/catalog';
  if (typeof window !== 'undefined') {
    window.location.href = appendLangToUrl(catalogUrl);
  }
}

/** On inline checkout host (/cart/checkout), navigateToView(null) re-opens checkout from URL. */
export function leaveCheckoutHostPage() {
  const homeUrl = getConfig().viewUrls?.home;
  if (homeUrl && typeof window !== 'undefined') {
    try {
      const targetPath = new URL(homeUrl, window.location.origin).pathname;
      if (window.location.pathname !== targetPath) {
        window.location.href = appendLangToUrl(homeUrl);
        return;
      }
    } catch { /* fall through */ }
  }
  openCatalogView();
}

export function openCartView() {
  const { viewUrls } = getConfig();
  const mode = resolveCartOpenMode();
  const cartUrl = viewUrls?.cart || '/cart';
  const drawerClose = typeof document !== 'undefined'
    ? document.querySelector<HTMLButtonElement>('.sw-cart-drawer-layer:not(.is-closing) .btn-close')
    : null;

  if (drawerClose) {
    drawerClose.click();
    return;
  }

  if (mode === 'side_drawer') {
    if (store.getAccountView() === 'cart') {
      store.setAccountView(null);
      return;
    }
  }

  requestHostMobileMenuClose('cart');

  if (mode === 'page') {
    if (typeof window !== 'undefined') {
      try {
        if (window.location.pathname.includes('/preview-proxy')) {
          const targetUrl = new URL(cartUrl, window.location.origin);
          const targetPath = normalizePathname(targetUrl.searchParams.get('path') ?? targetUrl.pathname);
          const currentUrl = new URL(window.location.href);
          const currentPath = normalizePathname(currentUrl.searchParams.get('path') ?? '/');
          if (currentPath !== targetPath) {
            currentUrl.searchParams.set('path', targetPath);
            window.location.href = currentUrl.toString();
          }
          return;
        }
        window.location.href = cartUrl;
        return;
      } catch { /* fall through */ }
    }
  }

  store.setAccountView('cart');
}

function normalizePathname(path: string): string {
  const p = (path || '/').trim().split('?')[0];
  const slash = p.startsWith('/') ? p : `/${p}`;
  return slash.replace(/\/+$/, '') || '/';
}

function tryParseUrlPath(url: string): string | null {
  if (!url) return null;
  try {
    return normalizePathname(new URL(url, window.location.origin).pathname);
  } catch {
    return null;
  }
}

export function resolveAuthViewFromUrl(url: string | null | undefined): AccountView {
  const raw = String(url ?? '');
  let path = tryParseUrlPath(raw);
  if (path === '/preview-proxy') {
    try {
      const proxyPath = new URL(raw, window.location.origin).searchParams.get('path');
      path = proxyPath ? normalizePathname(proxyPath) : path;
    } catch { /* keep parsed path */ }
  }
  if (!path) return null;
  if (path === '/signup' || path === '/my-account/signup') return 'signup';
  if (path === '/login') return 'checkout-login';
  if (path === '/reset-password') return 'reset-password';
  if (path === '/my-account' || path === '/account') return 'account';
  if (path === '/my-account/orders') return 'orders';
  if (path === '/my-account/tickets') return 'tickets';
  if (path === '/my-account/subscriptions' || path === '/my-account/subscription' || path === '/subscriptions') return 'subscriptions';
  if (path === '/my-account/bonuses') return 'bonus-account';
  if (path === '/my-account/bonuses/transactions' || path === '/my-account/bonus-transactions' || path === '/my-account/bonuses/trransactions') return 'bonus-transactions';
  if (path === '/my-account/referral' || path === '/my-account/bonuses/referrals') return 'referral-tree';
  if (path === '/my-account/discounts') return 'discounts';
  if (path === '/my-account/delete') return 'account-delete';
  return null;
}

/**
 * Navigate to a widget view. If `viewUrls` config maps this view to a host URL
 * and we're not already on that page, redirect. Otherwise set the view in store
 * (which triggers modal or inline render).
 *
 * For `null` (close/back): checks `viewUrls.home` — if set and we're on a
 * widget-managed page, redirects there. Otherwise just clears the view (closes modal).
 */
export function navigateToView(view: AccountView, opts?: { inline?: boolean }) {
  const { viewUrls } = getConfig();

  if (!view) {
    if (typeof window !== 'undefined') {
      const isPreviewProxy = window.location.pathname.includes('/preview-proxy');
      if (isPreviewProxy) {
        // In builder preview, closing modal must keep current preview path
        // (e.g. /contactus after login), not force redirect to home.
        store.setAccountView(null);
        return;
      }
    }
    const homeUrl = viewUrls?.home;
    if (homeUrl && typeof window !== 'undefined') {
      try {
        const target = new URL(homeUrl, window.location.origin).pathname;
        if (window.location.pathname !== target) {
          window.location.href = homeUrl;
          return;
        }
      } catch { /* fall through */ }
    }
    store.setAccountView(null);
    return;
  }

  // Guest opening subscriptions: do not navigate to subscriptions URL (full reload) only to bounce to login.
  if (view === 'subscriptions' && !store.getLoading() && !store.getAuth()) {
    markSubsAuthRedirectNotice();
    store.setReturnTo('subscriptions');
    return navigateToView('checkout-login', opts);
  }

  requestHostMobileMenuClose(view);

  if (!opts?.inline) {
    if (view === 'cart') {
      openCartView();
      return;
    }

    // Forgot-password is modal-only (email reset flow), never a page redirect.
    if (view === 'forgot-password') {
      store.setAccountView('forgot-password');
      return;
    }

    let url = viewUrls?.[view];
    const stayInSpa = view === 'order-success' || view === 'order-return';
    const authViews = new Set<AccountView>(['signup', 'checkout-login', 'reset-password']);
    const accountViewFallbackPath: Partial<Record<NonNullable<AccountView>, string>> = {
      account: '/my-account',
      orders: '/my-account/orders',
      tickets: '/my-account/tickets',
      subscriptions: '/my-account/subscriptions',
      'bonus-account': '/my-account/bonuses',
      'bonus-transactions': '/my-account/bonuses/transactions',
      'referral-tree': '/my-account/referral',
      discounts: '/my-account/discounts',
      'account-delete': '/my-account/delete',
    };

    if (url && typeof window !== 'undefined' && view && !authViews.has(view)) {
      try {
        const p = new URL(url, window.location.origin).pathname;
        const isAuthPath = p === '/signup' || p === '/login' || p === '/reset-password';
        if (isAuthPath) {
          url = accountViewFallbackPath[view] ?? undefined;
        }
      } catch { /* keep original url */ }
    }

    if (typeof window !== 'undefined') {
      try {
        const curPathname = window.location.pathname;
        const isPreviewProxy = curPathname.includes('/preview-proxy');

        if (isPreviewProxy && !stayInSpa && url) {
          // In preview: honor the saved viewUrls target. Falling back to the
          // built-in view path would make auth routes always open /login etc.
          const viewToPath: Record<string, string> = {
            signup: '/signup', 'checkout-login': '/login', 'reset-password': '/reset-password',
            cart: '/cart', checkout: '/checkout', 'checkout-auth': '/checkout/auth',
            account: '/my-account', orders: '/my-account/orders', tickets: '/my-account/tickets',
            subscriptions: '/my-account/subscriptions', 'bonus-account': '/my-account/bonuses',
            'bonus-transactions': '/my-account/bonuses/transactions', 'referral-tree': '/my-account/referral',
            discounts: '/my-account/discounts', 'account-delete': '/my-account/delete',
          };
          let normTgt = (viewToPath[view] ?? '/').replace(/^\/+/, '/') || '/';
          try {
            const targetUrl = new URL(url, window.location.origin);
            normTgt = normalizePathname(targetUrl.searchParams.get('path') || targetUrl.pathname || normTgt);
          } catch { /* keep fallback path */ }
          const curPathParam = new URLSearchParams(window.location.search).get('path') ?? '/';
          const normCur = normalizePathname(curPathParam);
          if (normCur !== normTgt) {
            const u = new URL(window.location.href);
            u.searchParams.set('path', normTgt);
            for (const key of LISTING_QUERY_KEYS) {
              u.searchParams.delete(key);
            }
            window.location.href = u.toString();
            return;
          }
          store.setAccountView(null);
          return;
        }

        if (url && !stayInSpa) {
          const targetUrl = new URL(url, window.location.origin);
          const curPath = curPathname;
          const tgtPath = targetUrl.pathname;
          const curPathParam = new URLSearchParams(window.location.search).get('path') ?? '/';
          const tgtPathParam = targetUrl.searchParams.get('path') ?? '/';
          const pathMatch = curPath === tgtPath && curPathParam === tgtPathParam;
          if (!pathMatch) {
            window.location.href = sanitizeViewRedirectUrl(url, view);
            return;
          }
          // Same page URL — still switch modal/overlay view (e.g. forgot-password → login).
        }
      } catch { /* fall through */ }
    }
  }

  store.setAccountView(view);
  const placeholderMap: Partial<Record<NonNullable<AccountView>, string>> = {
    signup: 'shop-signup-page',
    'reset-password': 'shop-reset-password-page',
    'checkout-login': 'shop-login-page',
    account: 'shop-profile-page',
    orders: 'shop-orders-page',
    tickets: 'shop-tickets-page',
    subscriptions: 'shop-subscriptions-page',
    'bonus-account': 'shop-bonuses-page',
    'bonus-transactions': 'shop-bonus-transactions-page',
    'referral-tree': 'shop-referral-page',
    discounts: 'shop-discounts-page',
    'account-delete': 'shop-delete-account-page',
  };
  const placeholderId = placeholderMap[view];
  if (placeholderId && typeof document !== 'undefined') {
    const el = document.getElementById(placeholderId);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
